import { makeStyles } from '@material-ui/core'
import { ChevronLeft as ChevronLeftIcon } from '@material-ui/icons'
import { unwrapResult } from '@reduxjs/toolkit'
import { useFormikContext } from 'formik'
import { merge, pick, pickBy } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'
import { Link } from 'react-router-dom'
import * as Yup from 'yup'

import {
  Button,
  Container,
  FormikTabForm,
  PageTitle,
  FIELD_REQUIRED,
  fileToBase64,
  mapArrayErrorsToObject,
} from 'src/modules/ui'

import AwardsForm, {
  validationSchema as awardsValidationSchema,
} from 'src/modules/ui/components/AwardsForm'
import SustainabilityForm, {
  initialValues as sustainbilityInitialValues,
  parseErrorsResponse as parseSustainabilityErrorsResponse,
  parseValuesForRequest as parseSustainabilityValuesForRequest,
  validationSchema as sustainabilityValidationSchema,
} from 'src/modules/ui/components/SustainabilityForm'

import Basic from './Basic'
import Nutritional from './Nutritional'

const useStyles = makeStyles(theme => ({
  backButton: {
    marginBottom: theme.spacing(1),
  },
  backLink: {
    textDecoration: 'inherit',
  },
}))

const ProductForm = ({
  allergenOptions,
  categoryOptions,
  dietaryOptions,
  nutritionalOptions,
  onSubmit,
  product,
  sustainabilityOptions,
  title,
}) => {
  const classes = useStyles()
  const routeBase = product ? `/products/${product.id}` : '/products/add'

  const tabs = [
    {
      label: 'Basic Info',
      path: `${routeBase}/basic`,
      render: () => <Basic categoryOptions={categoryOptions} />,
      formRemainingFields: true,
    },
    {
      label: 'Nutritional',
      path: `${routeBase}/nutritional`,
      render: () => (
        <Nutritional
          allergenOptions={allergenOptions}
          dietaryOptions={dietaryOptions}
          nutritionalOptions={nutritionalOptions}
        />
      ),
      formFields: ['dietaryInfo', 'nutritionalInfo', 'allergens'],
    },
    {
      label: 'Sustainability',
      path: `${routeBase}/sustainability`,
      render: () => (
        <SustainabilityForm sustainabilityOptions={sustainabilityOptions} />
      ),
      formFields: ['sustainability'],
    },
    {
      label: 'Awards',
      path: `${routeBase}/awards`,
      component: AwardsForm,
      formFields: ['awards'],
    },
  ]

  const reduceToObject = (acc, option) => ({
    ...acc,
    [option.id]: '',
  })

  const initialValues = {
    available: true,
    code: '',
    name: '',
    description: '',
    photo: '',
    unit: '',
    variablePrice: false,
    price: '',
    variablePriceUnit: 'kg',
    avEquivalentUnitSize: '',
    vatRate: '0',
    categoryId: '',
    subcategoryId: '',
    allergens: allergenOptions.reduce(reduceToObject, {}),
    awards: [],
    dietaryInfo: [],
    nutritionalInfo: nutritionalOptions.reduce(reduceToObject, {}),
    sustainability: sustainbilityInitialValues(
      product?.sustainability,
      sustainabilityOptions
    ),
  }
  if (product) {
    const {
      allergens,
      dietaryInfo,
      nutritionalInfo,
      sustainability,
      ...productRest
    } = product

    const existing = pickBy(
      pick(productRest, Object.keys(initialValues)),
      value => value !== null
    )
    merge(initialValues, existing)

    initialValues.categoryId = product.category.id
    initialValues.subcategoryId = product.subcategory.id

    nutritionalInfo.forEach(({ id, value }) => {
      initialValues.nutritionalInfo[id] = value
    })
    initialValues.dietaryInfo = dietaryInfo.map(({ id }) => String(id))
    allergens.forEach(({ id, type }) => {
      initialValues.allergens[id] = type
    })
  }

  const validationSchema = Yup.object().shape({
    code: Yup.string(),
    name: Yup.string().required(FIELD_REQUIRED),
    unit: Yup.string().required(FIELD_REQUIRED),
    price: Yup.number().typeError('Invalid number'),
    variablePriceUnit: Yup.string().when('variablePrice', {
      is: true,
      then: Yup.string().required(FIELD_REQUIRED),
    }),
    vatRate: Yup.number().typeError('Invalid number'),
    avEquivalentUnitSize: Yup.number().when('variablePrice', {
      is: true,
      then: Yup.number()
        .typeError('Invalid number')
        .moreThan(0)
        .required('Must provide an average unit size'),
    }),
    categoryId: Yup.string().required(FIELD_REQUIRED),
    subcategoryId: Yup.string().required(FIELD_REQUIRED),
    nutritionalInfo: Yup.object().shape(
      nutritionalOptions.reduce(
        (acc, option) => ({
          ...acc,
          [option.id]: Yup.string(),
        }),
        {}
      )
    ),
    sustainability: sustainabilityValidationSchema(sustainabilityOptions),
    awards: awardsValidationSchema(),
  })

  // If this is a File object then it's a new file so base64 encode it for the server.
  // If it's a string thing it's a link to the image so send it back to the server as
  // it hasn't changed. If it's falsey (ie "") then set to null to tell server to remove
  // it
  const getFileParam = async file =>
    file instanceof File ? await fileToBase64(file) : file || null

  const handleSubmit = async (values, { setErrors }) => {
    const {
      allergens,
      avEquivalentUnitSize,
      awards,
      categoryId,
      dietaryInfo,
      nutritionalInfo,
      photo,
      price,
      sustainability,
      variablePrice,
      variablePriceUnit,
      vatRate,
      ...valuesRest
    } = values

    const photoData = await getFileParam(photo)

    const allergenItems = Object.keys(allergens)
      .filter(key => !!allergens[key])
      .map(id => ({
        allergenId: id,
        type: allergens[id],
      }))

    const nutritionalInfoItems = Object.keys(nutritionalInfo)
      .filter(key => !!nutritionalInfo[key])
      .map(id => ({
        nutritionalInfoId: id,
        value: nutritionalInfo[id],
      }))

    const sustainabilityItems = await parseSustainabilityValuesForRequest(
      sustainability,
      sustainabilityOptions,
      categoryId
    )

    // Remove any sustainability options that do not apply to this
    // product because of it's category
    const params = {
      ...valuesRest,
      allergenItems,
      avEquivalentUnitSize: variablePrice ? avEquivalentUnitSize : null,
      awardItems: awards,
      dietaryInfoIds: dietaryInfo,
      nutritionalInfoItems,
      photoData,
      price: price || null,
      sustainabilityItems,
      variablePrice,
      variablePriceUnit: variablePrice ? variablePriceUnit : null,
      vatRate,
    }

    const result = await onSubmit(params)
    try {
      unwrapResult(result)
    } catch (err) {
      if (err.data) {
        // Relate field errors back to relations properly
        const {
          allergenItems,
          dietaryInfoIds,
          nutritionalInfoItems,
          photoData,
          sustainabilityItems,
          ...fieldErrors
        } = err.data

        // Map the array of relation errors back to the
        // object we have in Formik
        if (nutritionalInfoItems) {
          fieldErrors.nutritionalInfo = mapArrayErrorsToObject(
            nutritionalInfoItems,
            params.nutritionalInfoItems,
            'nutritionalInfoId',
            fieldError => fieldError.value
          )
        }
        if (sustainabilityItems) {
          fieldErrors.sustainability = parseSustainabilityErrorsResponse(
            sustainabilityItems,
            params.sustainabilityItems
          )
        }
        if (allergenItems) {
          fieldErrors.allergens = mapArrayErrorsToObject(
            allergenItems,
            params.allergenItems,
            'allergenId',
            fieldError => fieldError.type
          )
        }
        if (dietaryInfoIds) {
          fieldErrors.dietaryInfo = dietaryInfoIds
        }

        setErrors({
          photo: photoData,
          ...fieldErrors,
        })
      }
    }
    return result
  }

  return (
    <Container backLink>
      <Link className={classes.backLink} to={`/products`}>
        <Button
          className={classes.backButton}
          size="large"
          startIcon={<ChevronLeftIcon />}
          variant="text"
        >
          Products
        </Button>
      </Link>
      <FormikTabForm
        initialValues={initialValues}
        HeaderComponent={() => <FormTitle title={title} />}
        onSubmit={handleSubmit}
        tabs={tabs}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
      />
    </Container>
  )
}

const FormTitle = ({ title }) => {
  const { values } = useFormikContext()
  const pageTitle = values.name || title

  return <PageTitle>{pageTitle}</PageTitle>
}

ProductForm.propTypes = {
  allergenOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      group: PropTypes.string,
    })
  ),
  categoryOptions: PropTypes.array,
  dietaryOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
  nutritionalOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      units: PropTypes.string,
      position: PropTypes.number,
    })
  ),
  onSubmit: PropTypes.func,
  product: PropTypes.shape({
    available: PropTypes.bool,
    code: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    photo: PropTypes.string,
    unit: PropTypes.string,
    price: PropTypes.string,
    category: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    subcategory: PropTypes.shape({
      id: PropTypes.number,
      category: PropTypes.number,
      name: PropTypes.string,
    }),
  }),
  sustainabilityOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      image: PropTypes.string,
      name: PropTypes.string,
      group: PropTypes.string,
      question: PropTypes.string,
      appliesToSupplier: PropTypes.bool,
      appliesToProduct: PropTypes.bool,
      appliesToProductCategory: PropTypes.array,
    })
  ),
  title: PropTypes.string,
}

export default ProductForm
